var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-marketplace-panel> — the marketplace's panel body. One element,
 * every app, exactly as the AI panel is: the rail provides the frame, the app
 * mounts this inside it.
 *
 * It is a PAGE, not a drawer: the same BasePage chrome every detail view wears
 * (back link · title with icon · description · status badges · pill tabs · the
 * compact sticky bar with the scroll-to-top nub), so the rail reads as one more
 * viewport and not as a popover. The scrolling container is the panel's own,
 * named through BasePage's scroll hook; the back link closes the rail.
 *
 * Below the header: the shared search bar (<vitesse-search> with the same
 * `/command` grammar the symbol tree and the log view speak — /name, /author,
 * /official), then the catalog as a three-up grid of cards, each previewed as
 * the REAL component with generic demo data.
 *
 * The catalog is BUILT-IN for now: the Map is listed here; the app contributes
 * the rest (`extraListings`) because their previews need the app's widget host.
 * Entitlement gating is enforced where installs happen, not here — browsing is
 * open to everyone.
 *
 * Light DOM, like every panel body: the rail's <slot> keeps slotted content in
 * the app's own tree, so tokens and global SCSS apply without duplication.
 */
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { BasePage } from '../components/base-page';
import { statusBadge } from '../components/templates';
import { button } from '../components/base-buttons';
import { getSession } from '../session';
import { parseSearchInput, matchesFilters } from '../search';
import { Dashboards } from '@vitesse/proto/vitesse/v1/dashboard_pb.js';
import './vitesse-icon';
import './vitesse-search';
const authorOf = (l) => l.author ?? (l.official ? 'Vitesse' : '');
/** The `/command` vocabulary: the card's own fields. */
const COMMANDS = [
    {
        name: 'name',
        descKey: 'search.name',
        valueType: 'string',
        placeholder: '=map',
        match: (l, v) => !v || l.title.toLowerCase().includes(v.toLowerCase()),
        suggestValue: (l) => l.title,
    },
    {
        name: 'author',
        descKey: 'search.author',
        valueType: 'string',
        placeholder: '=vitesse',
        match: (l, v) => !v || authorOf(l).toLowerCase().includes(v.toLowerCase()),
        suggestValue: (l) => authorOf(l) || undefined,
    },
    {
        name: 'official',
        descKey: 'search.official',
        valueType: 'boolean',
        placeholder: '',
        match: (l, v) => (v === 'false' ? !l.official : !!l.official),
    },
];
const textMatch = (l, q) => {
    const s = q.toLowerCase();
    return l.title.toLowerCase().includes(s) || l.summary.toLowerCase().includes(s) || authorOf(l).toLowerCase().includes(s);
};
/** Generic demo fleet for the Map preview — deliberately fictional places (a
 *  preview shows the SHAPE of the widget, never anyone's real plant). */
const DEMO_GATEWAYS = [
    { deviceFingerprint: 'demo-1', orgId: '', displayName: 'Press line', status: 'online', geo: 'Lyon, FR', lat: 45.76, lng: 4.84 },
    { deviceFingerprint: 'demo-2', orgId: '', displayName: 'Packaging', status: 'online', geo: 'Detroit, US', lat: 42.33, lng: -83.05 },
    { deviceFingerprint: 'demo-3', orgId: '', displayName: 'Old rig', status: 'offline', geo: 'Osaka, JP', lat: 34.69, lng: 135.5 },
];
const DEMO_PINS = [{ label: 'HQ', lat: 48.85, lng: 2.35 }];
let VitesseMarketplacePanel = class VitesseMarketplacePanel extends BasePage {
    constructor() {
        super(...arguments);
        this.kind = 'widgets';
        /**
         * Listings the APP contributes — the widgets it can render a genuine preview of (the sandboxed
         * catalogue widgets need the app's widget host, which this app-agnostic panel does not have).
         * Listed after the built-in map, same card, same download.
         */
        this.extraListings = [];
        this.query = '';
        /** The Map preview's heavy deps (maplibre + deck) load on demand — the panel
         *  itself must stay light so opening the rail is instant everywhere. */
        this.mapReady = false;
    }
    connectedCallback() {
        super.connectedCallback();
        void import('./vitesse-world-map').then(() => (this.mapReady = true));
    }
    /** The panel scrolls itself: the rail is a fixed column and this is its only scroller. */
    scrollContainer() {
        return this.renderRoot.querySelector('.marketplace-panel');
    }
    listings() {
        return [
            {
                id: 'map',
                kind: 'widgets',
                official: true,
                title: t('marketplace.mapTitle'),
                summary: t('marketplace.mapSummary'),
                widget: { source: 'native:map', config: {} },
                preview: () => this.mapReady
                    ? html `<div class="marketplace-preview marketplace-preview-map">
                <vitesse-world-map
                  .gateways=${DEMO_GATEWAYS}
                  .cloudServers=${[]}
                  .markers=${DEMO_PINS}
                  .hotkeys=${false}
                ></vitesse-world-map>
              </div>`
                    : html `<div class="marketplace-preview"></div>`,
            },
            ...this.extraListings,
        ];
    }
    /** May the viewer install a widget? dashboard:create is the "bring an item into
     *  the org" gate the marketplace uses (browsing stays open to everyone; the
     *  download is what needs the dashboards entitlement + the permission). */
    get canInstall() {
        return getSession()?.allowsRpc(Dashboards.method.create) !== false;
    }
    /** "Download onto a dashboard": hand the widget to the app, which opens a
     *  dashboard picker and adds it as a tile — the SAME install the AI's
     *  dashboard-designer performs. The panel is app-agnostic (it has no dashboard
     *  managers), so it dispatches; the shell owns the picker + the write. */
    install(l) {
        if (!l.widget)
            return;
        this.dispatchEvent(new CustomEvent('vitesse-marketplace-install', {
            detail: { id: l.id, title: l.title, source: l.widget.source, config: l.widget.config },
            bubbles: true,
            composed: true,
        }));
    }
    // ── BasePage hooks ─────────────────────────────────────────────────────────
    pageTitle() {
        return t('marketplace.title');
    }
    titleIcon() {
        return 'store';
    }
    /** The back link closes the rail — the marketplace's "parent" is wherever you were. */
    onBack() {
        this.dispatchEvent(new CustomEvent('vitesse-toggle-marketplace', { bubbles: true, composed: true }));
    }
    backLabel() {
        return t('marketplace.close');
    }
    renderDescription() {
        return t('marketplace.description');
    }
    renderBadges() {
        const all = this.listings().filter((l) => l.kind === this.kind);
        const official = all.filter((l) => l.official).length;
        return html `${statusBadge('blue', t('marketplace.itemsCount', { n: all.length }))}
    ${statusBadge('green', t('marketplace.officialCount', { n: official }))}
    ${statusBadge('gray', t('marketplace.communityCount', { n: all.length - official }))}`;
    }
    tabs() {
        return [
            { id: 'models', label: t('marketplace.tabModels') },
            { id: 'widgets', label: t('marketplace.tabWidgets') },
        ];
    }
    activeTabId() {
        return this.kind;
    }
    onTabChange(id) {
        this.kind = id;
    }
    /** The page chrome inside the panel's own scroller. */
    render() {
        return html `<div class="marketplace-panel">${super.render()}</div>`;
    }
    renderContent() {
        const canInstall = this.canInstall;
        const parsed = parseSearchInput(this.query, COMMANDS);
        const rows = this.listings().filter((l) => l.kind === this.kind && matchesFilters(l, parsed, undefined, textMatch));
        return html `
      <div class="marketplace-panel-search">
        <vitesse-search
          placeholder=${t('marketplace.searchPlaceholder')}
          .commands=${COMMANDS}
          .iterate=${() => this.listings().filter((l) => l.kind === this.kind)}
          .textMatch=${textMatch}
          .onQuery=${(query) => (this.query = query)}
        ></vitesse-search>
      </div>
      ${rows.length
            ? html `<div class="marketplace-grid">
            ${rows.map((l) => html `<div class="marketplace-card">
                ${l.preview()}
                ${l.widget && canInstall
                ? html `<div class="marketplace-card-install">
                      ${button({ icon: 'download', title: t('marketplace.download'), color: 'blue', size: 'sm', onClick: () => this.install(l) })}
                    </div>`
                : nothing}
                <div class="marketplace-card-body">
                  <div class="marketplace-card-text">
                    <div class="marketplace-card-title">
                      <span class="marketplace-card-name">${l.title}</span>
                      ${l.official
                ? html `<span class="marketplace-official" title=${t('marketplace.officialTitle')}>
                            <vitesse-icon name="badge-check" size="14"></vitesse-icon>${t('marketplace.official')}
                          </span>`
                : nothing}
                    </div>
                    <div class="marketplace-card-summary" title=${l.summary}>${l.summary}</div>
                    ${authorOf(l) ? html `<div class="marketplace-card-author">${t('marketplace.by', { author: authorOf(l) })}</div>` : nothing}
                  </div>
                </div>
              </div>`)}
          </div>`
            : html `<div class="marketplace-panel-empty">
            ${this.kind === 'models' ? t('marketplace.modelsEmpty') : t('marketplace.noResults')}
          </div>`}
    `;
    }
};
__decorate([
    state()
], VitesseMarketplacePanel.prototype, "kind", void 0);
__decorate([
    property({ attribute: false })
], VitesseMarketplacePanel.prototype, "extraListings", void 0);
__decorate([
    state()
], VitesseMarketplacePanel.prototype, "query", void 0);
__decorate([
    state()
], VitesseMarketplacePanel.prototype, "mapReady", void 0);
VitesseMarketplacePanel = __decorate([
    customElement('vitesse-marketplace-panel')
], VitesseMarketplacePanel);
export { VitesseMarketplacePanel };
//# sourceMappingURL=vitesse-marketplace-panel.js.map